/**
 * Menus deroulants batis sur `<details>` : ouverture au clavier sans code, pas
 * de piege de focus. Ils se referment au clic exterieur, sur Echap (le menu
 * seul, pas la couche qui le contient) et apres le choix d'une entree.
 */
import { Children, cloneElement, useEffect, useId, useRef, useState } from 'react';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { auPremierPlan } from './couche';

/** Classes qui effacent le triangle natif d'un `<summary>` (Safari compris). */
export const CLASSES_SOMMAIRE =
  'cursor-pointer list-none marker:content-none [&::-webkit-details-marker]:hidden';

/**
 * Referme un `<details>` deroulant (menu, selection multiple) : sur Echap, au
 * clic exterieur et, avec `focusExterieur`, quand le focus en sort.
 */
export function useRepliDetails(
  refDetails: RefObject<HTMLDetailsElement | null>,
  { focusExterieur = false }: { focusExterieur?: boolean } = {},
): void {
  useEffect(() => {
    const surTouche = (evenement: KeyboardEvent): void => {
      const details = refDetails.current;
      if (evenement.key !== 'Escape' || !details?.open) return;
      const focusDedans = details.contains(document.activeElement);
      details.open = false;
      // Reste ouvert sous une fenetre ouverte ensuite (au clavier) : il se
      // referme sans lui prendre la frappe ni le focus, elle est au premier plan.
      if (!auPremierPlan(details)) return;
      // Frappe marquee comme consommee : la couche qui contient le deroulant
      // (tiroir, fenetre) la laisse passer au lieu de se fermer elle aussi.
      evenement.preventDefault();
      // Le contenu replie emporterait le focus : il revient au sommaire. Parti
      // ailleurs (raccourci, autre champ), il y reste.
      if (focusDedans) details.querySelector('summary')?.focus();
    };
    const surExterieur = (evenement: Event): void => {
      const details = refDetails.current;
      if (!details?.open) return;
      if (evenement.target instanceof Node && details.contains(evenement.target)) return;
      details.open = false;
    };
    // Appuyer sur un texte (le nom d'une option) donne le focus au conteneur
    // focalisable le plus proche (`main`, panneau) : ce n'est pas une sortie.
    // Refermer en plein clic ne cocherait rien et fait planter Chromium ; le clic
    // exterieur a son propre ecouteur.
    const surFocus = (evenement: FocusEvent): void => {
      const details = refDetails.current;
      if (details && evenement.target instanceof Node && evenement.target.contains(details)) return;
      surExterieur(evenement);
    };
    // Phase de capture : le deroulant passe avant les couches, qui ecoutent en
    // bouillonnement, quel que soit l'ordre d'inscription des ecouteurs.
    document.addEventListener('keydown', surTouche, true);
    document.addEventListener('pointerdown', surExterieur);
    if (focusExterieur) document.addEventListener('focusin', surFocus);
    return () => {
      document.removeEventListener('keydown', surTouche, true);
      document.removeEventListener('pointerdown', surExterieur);
      document.removeEventListener('focusin', surFocus);
    };
  }, [refDetails, focusExterieur]);
}

export function MenuDeroulant({
  declencheur,
  children,
  largeur = 'w-64',
  alignement = 'droite',
  direction = 'bas',
  libelle,
  titre,
  classeDeclencheur = `bouton bouton-md bouton-secondaire ${CLASSES_SOMMAIRE}`,
}: {
  declencheur: ReactNode;
  children: ReactNode;
  largeur?: string;
  alignement?: 'gauche' | 'droite';
  /** `haut` pour un menu ancre en bas d'ecran (bloc utilisateur). */
  direction?: 'bas' | 'haut';
  /** Nom accessible du declencheur quand il n'affiche qu'une icone. */
  libelle?: string;
  /** Infobulle du declencheur a icone seule, comme celle d'un `BoutonIcone` voisin. */
  titre?: string;
  classeDeclencheur?: string;
}) {
  const refDetails = useRef<HTMLDetailsElement>(null);
  useRepliDetails(refDetails);

  return (
    <details ref={refDetails} className="relative">
      <summary aria-label={libelle} title={titre} className={classeDeclencheur}>
        {declencheur}
      </summary>
      <div
        className={`surface-menu absolute z-20 ${largeur} ${
          alignement === 'droite' ? 'right-0' : 'left-0'
        } ${direction === 'haut' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'} animate-apparition p-1.5`}
      >
        {children}
      </div>
    </details>
  );
}

/** Element de menu : meme gabarit pour toutes les actions, `danger` isole par un separateur. */
export function ElementMenu({
  children,
  onClick,
  desactive = false,
  icone,
  ton = 'normal',
}: {
  children: ReactNode;
  onClick: () => void;
  desactive?: boolean;
  icone?: ReactNode;
  ton?: 'normal' | 'danger';
}) {
  return (
    <button
      type="button"
      disabled={desactive}
      onClick={(evenement) => {
        // Refermer avant d'agir : sinon le menu recouvre la fenetre de
        // confirmation qui s'ouvre, et le focus rendu a la fermeture de
        // celle-ci viserait un bouton masque.
        const details = evenement.currentTarget.closest('details');
        if (details) {
          details.open = false;
          details.querySelector('summary')?.focus();
        }
        onClick();
      }}
      className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-base disabled:cursor-not-allowed disabled:opacity-50 ${
        ton === 'danger'
          ? 'text-danger-700 hover:bg-danger-50'
          : 'text-encre-douce hover:bg-surface-appuyee hover:text-encre'
      }`}
    >
      {icone}
      {children}
    </button>
  );
}

export function SeparateurMenu() {
  return <hr className="my-1.5 border-bordure" />;
}

/**
 * Infobulle au survol et au focus clavier.
 * Le contenu decrit le declencheur lui-meme (`aria-describedby` pose sur
 * l'enfant focalisable) : il est lu, pas seulement vu. Echap la masque sans
 * deplacer le focus (WCAG 1.4.13) ; elle revient a la visite suivante.
 */
export function Infobulle({
  contenu,
  children,
  className = '',
}: {
  contenu: ReactNode;
  /** Declencheur focalisable unique (bouton, lien). */
  children: ReactElement<{ 'aria-describedby'?: string }>;
  className?: string;
}) {
  const id = useId();
  const refConteneur = useRef<HTMLSpanElement>(null);
  const [survol, setSurvol] = useState(false);
  const [focus, setFocus] = useState(false);
  const [masquee, setMasquee] = useState(false);
  const visible = (survol || focus) && !masquee;

  useEffect(() => {
    if (!survol && !focus) setMasquee(false);
  }, [survol, focus]);

  useEffect(() => {
    if (!visible) return undefined;
    const surTouche = (evenement: KeyboardEvent): void => {
      const conteneur = refConteneur.current;
      if (evenement.key !== 'Escape' || !conteneur || !auPremierPlan(conteneur)) return;
      // Consommee : la fenetre qui contient l'infobulle reste ouverte.
      evenement.preventDefault();
      setMasquee(true);
    };
    document.addEventListener('keydown', surTouche, true);
    return () => document.removeEventListener('keydown', surTouche, true);
  }, [visible]);

  const declencheur = Children.only(children);
  const decritPar = [declencheur.props['aria-describedby'], id].filter(Boolean).join(' ');

  return (
    <span
      ref={refConteneur}
      className={`group/info relative inline-flex ${className}`}
      onMouseEnter={() => setSurvol(true)}
      onMouseLeave={() => setSurvol(false)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
    >
      {cloneElement(declencheur, { 'aria-describedby': decritPar })}
      <span
        role="tooltip"
        id={id}
        hidden={masquee}
        className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-1.5 hidden w-max max-w-64 -translate-x-1/2 rounded-md bg-neutre-900 px-2.5 py-1.5 text-xs leading-snug font-normal tracking-normal normal-case text-neutre-0 shadow-flottant group-hover/info:block group-focus-within/info:block"
      >
        {contenu}
      </span>
    </span>
  );
}
